import { handleImgErrors } from "./modules/handleImgErrors";
import { initUsStatesOptOut } from "./modules/us-states-opt-out";
import { getApiToken } from "./modules/tokenUtils";
import { formatFutureRelative } from "./modules/time-utils";
import { isNetworkErrorMessage } from "./modules/network-errors";
import { init, setTag, setContext, captureException, captureMessage } from "@sentry/browser";

// Fraction of pure network-failure events (offline users, flaky
// connections) that is kept. Kept events carry the
// network_error_sampled tag so outage trends stay visible despite the
// reduced volume.
const NETWORK_ERROR_SAMPLE_RATE = 0.01;

init({
  dsn: "https://40e19bafa75d417ead2733a00ab3e30f@sentry.interpals.net/8",
  ignoreErrors: [
    "Non-Error exception captured",
    "Non-Error promise rejection captured",
    // ads-init.js catches this synchronously in refreshGptSlots, but GPT's
    // internal catch-and-rethrow wrapper around pbjs.setTargetingForGPTAsync
    // can resurface the TypeError to a setTimeout-wrapped callback where the
    // TryCatch integration captures it as a handled event. GAM still refreshes
    // (direct demand only) so the failure is cosmetic.
    "pbjs.setTargetingForGPTAsync is not a function",
    // Benign per the ResizeObserver spec: browsers emit these when
    // observation callbacks cannot all be delivered within one frame.
    // Emitted with type Error, so message matching here is the filter
    // point (an exception-type matcher would miss them).
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications.",
    // AdSense TagErrors from adsbygoogle.push(): the async variants throw
    // inside Google's own deferred queue drain, outside any try/catch at
    // the push site, so they can only be filtered here. Both indicate a
    // slot that cannot be filled (zero-width container / already-filled
    // ins element), not a page failure.
    "No slot size for availableWidth=",
    "already have ads in them",
    // Firefox cross-origin SecurityError surfaced when Alpine's global
    // MutationObserver (and the Lucide icon init) walk into a node an ad iframe
    // injected from another origin. The stack points at our code, but the cause
    // is the cross-origin node, so it is not actionable on our end.
    "Permission denied to access property"
  ],
  denyUrls: [
    // Code injected by browser extensions, including the blob: script
    // URLs extensions use to run code in the page context.
    /^blob:/,
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//
  ],
  beforeSend(event) {
    try {
      // Headless-browser traffic (scrapers, bots) is not user-facing
      // breakage and can produce huge event volumes from a single host.
      const userAgent = (typeof navigator !== "undefined" && navigator.userAgent) || "";
      if (userAgent.indexOf("HeadlessChrome") !== -1) {
        return null;
      }
      // Sample pure network failures instead of dropping them outright
      // so outage trends stay visible.
      const values = event.exception && event.exception.values;
      const primary = values && values.length > 0 ? values[values.length - 1] : null;
      if (primary && primary.type === "TypeError" && isNetworkErrorMessage(primary.value)) {
        if (Math.random() >= NETWORK_ERROR_SAMPLE_RATE) {
          return null;
        }
        event.tags = { ...event.tags, network_error_sampled: "1" };
      }
      return event;
    } catch {
      // beforeSend must never throw; deliver the event unfiltered.
      return event;
    }
  }
});

setTag("user_id", window.userId ?? "none");

setContext("user", {
  id: window.userId ?? "none"
});

// Expose Sentry methods for non-module scripts (e.g. thread-view.js, adsense-init.js)
window.Sentry = { captureException, captureMessage };

handleImgErrors();
initUsStatesOptOut();

// Fetch api token on page load if not present in local storage
// document.addEventListener("DOMContentLoaded", async () => {
window.getApiToken = getApiToken;
window.formatFutureRelative = formatFutureRelative;
//   api.init(token);

  // Make api object available globally (for jquery)
  // window.api = api.methods;
// });

console.log("v:6");

// Re-register the Web Push token after page load when permission is already
// granted. Defers behind requestIdleCallback so it never competes with first
// paint, and stays a no-op for guests and browsers without the APIs.
if (
  typeof window !== "undefined"
  && window.userId
  && "Notification" in window
  && Notification.permission === "granted"
  && "serviceWorker" in navigator
  && "PushManager" in window
) {
  const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
  schedule(() => {
    import("./modules/web-push")
      .then(({ registerForWebPush }) => registerForWebPush())
      .catch((err) => {
        if (window.Sentry && typeof window.Sentry.captureException === "function") {
          window.Sentry.captureException(err);
        }
      });
  });
}
